import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "drawer",
    "panel",
    "body",
    "footer",
    "section",
    "tab",
    "title",
    "form",
    "saveButton",
    "status",
    "visibilitySummary",
    "visibilityNote",
    "openingTimesToggle",
    "matchTimesToggle",
    "defaultNote",
    "customShell",
    "sameNote",
    "orderingFields",
    "warning",
    "warningMessage",
  ]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleBodyScroll = this.handleScroll.bind(this)
    this.activeTrigger = null
    this.previousMatchTimes = this.hasMatchTimesToggleTarget ? this.matchTimesToggleTarget.checked : true
    if (this.hasBodyTarget) {
      this.bodyTarget.addEventListener("scroll", this.handleBodyScroll)
    }
    if (this.hasDrawerTarget && this.drawerTarget.classList.contains("is-open")) {
      document.body.classList.add("menu-v2-drawer-open")
      document.addEventListener("keydown", this.handleKeydown)
    }
    this.syncScheduleFields()
    this.updateScrollCues()
    this.updateActiveTab()
  }

  disconnect() {
    if (this.hasBodyTarget) {
      this.bodyTarget.removeEventListener("scroll", this.handleBodyScroll)
    }
    document.removeEventListener("keydown", this.handleKeydown)
    this.unlockBody()
  }

  open(event) {
    event.preventDefault()

    this.activeTrigger = event.currentTarget
    this.populateFromTrigger(event.currentTarget)

    document.body.classList.add("menu-v2-drawer-open")
    document.addEventListener("keydown", this.handleKeydown)

    this.drawerTarget.classList.add("is-open")
    this.drawerTarget.setAttribute("aria-hidden", "false")
    this.syncScheduleFields()
    const scrollKey = event.currentTarget.dataset.drawerScrollKey
    if (this.hasBodyTarget) this.bodyTarget.scrollTop = 0
    requestAnimationFrame(() => {
      if (scrollKey) {
        this.scrollToKey(scrollKey, "auto")
      }
      this.handleScroll()
    })
    if (this.hasPanelTarget) this.panelTarget.focus()
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.hasDrawerTarget || !this.drawerTarget.classList.contains("is-open")) return

    this.drawerTarget.classList.remove("is-open")
    this.drawerTarget.setAttribute("aria-hidden", "true")
    document.removeEventListener("keydown", this.handleKeydown)
    this.unlockBody()

    if (this.activeTrigger) this.activeTrigger.focus()
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  handleScroll() {
    this.updateScrollCues()
    this.updateActiveTab()
  }

  populateFromTrigger(trigger) {
    const {
      drawerTitle,
      drawerUpdateUrl,
      menuName,
      visibilitySummary,
      visibilityNote,
      useOpeningTimes,
      matchTimes,
      viewDays,
      viewStartTime,
      viewEndTime,
      orderStartTime,
      orderEndTime,
      onlineEnabled,
      posEnabled,
      selectedServiceTypeIds,
      advancedWarning,
      advancedWarningMessage,
    } = trigger.dataset

    if (this.hasTitleTarget) {
      const baseTitle = drawerTitle || "Edit Schedule"
      this.titleTarget.textContent = menuName ? `${baseTitle} · ${menuName}` : baseTitle
    }

    if (this.hasFormTarget && drawerUpdateUrl) {
      this.formTarget.action = drawerUpdateUrl
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false
    }

    this.resetStatus()

    if (this.hasVisibilitySummaryTarget) {
      this.visibilitySummaryTarget.textContent = visibilitySummary || "Available during restaurant opening hours"
    }

    if (this.hasVisibilityNoteTarget) {
      this.visibilityNoteTarget.textContent = visibilityNote || "This will control when customers can see the menu."
    }

    if (this.hasOpeningTimesToggleTarget) {
      this.openingTimesToggleTarget.checked = this.castBoolean(useOpeningTimes)
    }

    if (this.hasMatchTimesToggleTarget) {
      this.matchTimesToggleTarget.checked = this.castBoolean(matchTimes)
    }

    this.previousMatchTimes = this.hasMatchTimesToggleTarget ? this.matchTimesToggleTarget.checked : true

    this.setCheckboxGroup("menu_schedule_editor[service_type_ids][]", this.parseArray(selectedServiceTypeIds))
    this.setCheckboxGroup("menu_schedule_editor[view_days][]", this.parseArray(viewDays))
    this.setCheckboxValue("menu_schedule_editor[online_enabled]", this.castBoolean(onlineEnabled))
    this.setCheckboxValue("menu_schedule_editor[pos_enabled]", this.castBoolean(posEnabled))
    this.setInputValue("schedule-view-start", viewStartTime)
    this.setInputValue("schedule-view-end", viewEndTime)
    this.setInputValue("schedule-order-start", orderStartTime)
    this.setInputValue("schedule-order-end", orderEndTime)
    this.setWarningState(this.castBoolean(advancedWarning), advancedWarningMessage)
  }

  syncScheduleFields() {
    const useOpeningTimes = this.hasOpeningTimesToggleTarget ? this.openingTimesToggleTarget.checked : true
    const matchTimes = this.hasMatchTimesToggleTarget ? this.matchTimesToggleTarget.checked : true
    const shouldCopyViewTimes = !useOpeningTimes && this.previousMatchTimes && !matchTimes

    if (this.hasDefaultNoteTarget) {
      this.defaultNoteTarget.hidden = !useOpeningTimes
    }

    if (this.hasCustomShellTarget) {
      this.customShellTarget.hidden = useOpeningTimes
    }

    if (this.hasSameNoteTarget) {
      this.sameNoteTarget.hidden = useOpeningTimes || !matchTimes
    }

    if (this.hasOrderingFieldsTarget) {
      this.orderingFieldsTarget.hidden = useOpeningTimes || matchTimes
    }

    if (shouldCopyViewTimes) {
      this.copyViewTimesToOrdering()
    }

    this.previousMatchTimes = matchTimes
    requestAnimationFrame(() => this.handleScroll())
  }

  async submitForm(event) {
    event.preventDefault()

    if (!this.hasFormTarget) return

    const formData = new FormData(this.formTarget)
    this.setStatus("Saving…")

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true
    }

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: {
          "Accept": "application/json",
        },
        body: this.buildRequestFormData(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to save")
      }

      this.applySavedState(data.state || {})
      this.setStatus(data.message || "Saved!", "success")
    } catch (error) {
      console.error(error)
      this.setStatus(`Error: ${error.message}`, "error")
    } finally {
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = false
      }
    }
  }

  unlockBody() {
    if (!document.querySelector(".menu-v2-schedule-drawer.is-open")) {
      document.body.classList.remove("menu-v2-drawer-open")
    }
  }

  setInputValue(id, value) {
    const input = document.getElementById(id)
    if (input && value) input.value = value
  }

  setCheckboxGroup(name, values) {
    const set = new Set(values)
    document.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      input.checked = set.has(input.value)
    })
  }

  setCheckboxValue(name, checked) {
    document.querySelectorAll(`input[name="${name}"]`).forEach((input) => {
      input.checked = !!checked
    })
  }

  hasCheckbox(name) {
    return document.querySelector(`input[name="${name}"]`) !== null
  }

  parseArray(value) {
    if (!value) return []

    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  castBoolean(value) {
    return value === true || value === "true"
  }

  buildRequestFormData(formData) {
    const requestData = new FormData()

    for (const [key, value] of formData.entries()) {
      requestData.append(key, value)
    }

    requestData.set("_method", "patch")

    if (!requestData.has("menu_schedule_editor[match_times]")) {
      requestData.append("menu_schedule_editor[match_times]", "0")
    }

    if (!requestData.has("menu_schedule_editor[use_opening_times]")) {
      requestData.append("menu_schedule_editor[use_opening_times]", "0")
    }

    if (this.hasCheckbox("menu_schedule_editor[online_enabled]") && !requestData.has("menu_schedule_editor[online_enabled]")) {
      requestData.append("menu_schedule_editor[online_enabled]", "0")
    }

    if (this.hasCheckbox("menu_schedule_editor[pos_enabled]") && !requestData.has("menu_schedule_editor[pos_enabled]")) {
      requestData.append("menu_schedule_editor[pos_enabled]", "0")
    }

    return requestData
  }

  copyViewTimesToOrdering() {
    const viewStart = document.getElementById("schedule-view-start")?.value
    const viewEnd = document.getElementById("schedule-view-end")?.value

    this.setInputValue("schedule-order-start", viewStart)
    this.setInputValue("schedule-order-end", viewEnd)
  }

  applySavedState(state) {
    const menuId = state.menu_id || this.activeTrigger?.dataset.menuId
    if (!menuId) return

    this.updateText(`menu-cards2-badge-label-${menuId}`, state.badge)
    this.updateText(`menu-cards2-summary-${menuId}`, state.summary)
    this.updateText(`menu-cards2-detail-${menuId}`, state.detail)
    this.updateText(`menu-cards2-service-types-${menuId}`, state.collapsed_service_type_summary || state.service_type_summary)
    this.updateText(`menu-cards2-service-types-full-${menuId}`, state.service_type_summary)

    this.updateClass(`menu-cards2-badge-${menuId}`, "custom", state.pill_class === "custom")
    this.updateHidden(`menu-cards2-status-usage-${menuId}`, !state.pos_selection_enabled)
    this.updateHidden(`menu-cards2-status-usage-online-${menuId}`, !state.online_enabled)
    this.updateHidden(`menu-cards2-status-usage-pos-${menuId}`, !state.pos_enabled)
    this.updateHidden(`menu-cards2-warning-wrap-${menuId}`, !state.has_visibility_problems)
    this.updateText(`menu-cards2-warning-count-${menuId}`, state.visibility_warning_count_label || "")
    this.updateText(`menu-cards2-warning-title-${menuId}`, state.visibility_warning_title || "")
    this.updateList(`menu-cards2-warning-list-${menuId}`, state.visibility_problems || [])
    this.updateHidden(`menu-cards2-usage-online-badge-${menuId}`, !state.online_enabled)
    this.updateHidden(`menu-cards2-usage-pos-badge-${menuId}`, !state.pos_enabled)
    this.updateHidden(`menu-cards2-usage-badges-${menuId}`, !state.pos_selection_enabled)

    ;[
      document.getElementById(`menu-cards2-action-${menuId}`),
      document.getElementById(`menu-cards2-dropdown-action-${menuId}`),
      document.getElementById(`menu-cards2-status-usage-online-${menuId}`),
      document.getElementById(`menu-cards2-status-usage-pos-${menuId}`),
    ].forEach((trigger) => {
      if (!trigger) return

      trigger.dataset.visibilitySummary = state.drawer_summary || ""
      trigger.dataset.visibilityNote = state.drawer_note || ""
      trigger.dataset.useOpeningTimes = String(!!state.use_opening_times)
      trigger.dataset.matchTimes = String(!!state.match_times)
      trigger.dataset.viewDays = JSON.stringify(state.view_days || [])
      trigger.dataset.viewStartTime = state.view_start_time || ""
      trigger.dataset.viewEndTime = state.view_end_time || ""
      trigger.dataset.orderStartTime = state.order_start_time || ""
      trigger.dataset.orderEndTime = state.order_end_time || ""
      trigger.dataset.onlineEnabled = String(!!state.online_enabled)
      trigger.dataset.posEnabled = String(!!state.pos_enabled)
      trigger.dataset.selectedServiceTypeIds = JSON.stringify(state.selected_service_type_ids || [])
      trigger.dataset.advancedWarning = String(!!state.advanced_warning)
      trigger.dataset.advancedWarningMessage = state.advanced_warning_message || ""
    })

    if (this.hasVisibilitySummaryTarget) {
      this.visibilitySummaryTarget.textContent = state.drawer_summary || ""
    }

    if (this.hasVisibilityNoteTarget) {
      this.visibilityNoteTarget.textContent = state.drawer_note || ""
    }

    this.setWarningState(!!state.advanced_warning, state.advanced_warning_message)
  }

  updateText(id, value) {
    const element = document.getElementById(id)
    if (element && value !== undefined) {
      element.textContent = value
    }
  }

  updateList(id, items) {
    const element = document.getElementById(id)
    if (!element) return

    element.innerHTML = ""
    items.forEach((item) => {
      const li = document.createElement("li")
      li.textContent = item
      element.appendChild(li)
    })
  }

  updateClass(id, className, enabled) {
    const element = document.getElementById(id)
    if (!element) return

    element.classList.toggle(className, !!enabled)
  }

  updateHidden(id, hidden) {
    const element = document.getElementById(id)
    if (!element) return

    element.hidden = !!hidden
  }

  resetStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.classList.remove("is-success", "is-error")
  }

  setWarningState(enabled, message = "") {
    if (this.hasWarningTarget) {
      this.warningTarget.hidden = !enabled
    }

    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.textContent = message || ""
    }
  }

  updateScrollCues() {
    if (!this.hasBodyTarget) return

    const { scrollTop, clientHeight, scrollHeight } = this.bodyTarget
    const scrollable = scrollHeight > clientHeight + 4
    const atBottom = scrollTop + clientHeight >= scrollHeight - 4

    if (this.hasFooterTarget) {
      this.footerTarget.classList.toggle("has-more-below", scrollable && !atBottom)
    }

  }

  scrollToSection(event) {
    event.preventDefault()
    if (!this.hasBodyTarget) return

    const sectionKey = event.currentTarget.dataset.sectionKey
    const section = this.sectionTargets.find((item) => item.dataset.sectionKey === sectionKey)
    if (!section) return

    this.bodyTarget.scrollTo({
      top: Math.max(this.sectionScrollTop(section) - 12, 0),
      behavior: "smooth",
    })
  }

  scrollToKey(scrollKey, behavior = "smooth") {
    if (!this.hasBodyTarget || !scrollKey) return

    const section = this.bodyTarget.querySelector(`[data-scroll-key="${scrollKey}"]`)
    if (!section) return

    this.bodyTarget.scrollTo({
      top: Math.max(this.sectionScrollTop(section) - 12, 0),
      behavior,
    })
  }

  updateActiveTab() {
    if (!this.hasBodyTarget || !this.hasTabTarget || !this.hasSectionTarget) return

    const scrollAnchor = this.bodyTarget.scrollTop + 24
    let activeKey = this.sectionTargets[0]?.dataset.sectionKey

    this.sectionTargets.forEach((section) => {
      if (this.sectionScrollTop(section) <= scrollAnchor) {
        activeKey = section.dataset.sectionKey
      }
    })

    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.sectionKey === activeKey
      tab.classList.toggle("is-active", isActive)
      tab.classList.toggle("active", isActive)
    })
  }

  sectionScrollTop(section) {
    const bodyRect = this.bodyTarget.getBoundingClientRect()
    const sectionRect = section.getBoundingClientRect()

    return this.bodyTarget.scrollTop + (sectionRect.top - bodyRect.top)
  }

  setStatus(message, state = null) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("is-success", "is-error")

    if (state === "success") {
      this.statusTarget.classList.add("is-success")
    } else if (state === "error") {
      this.statusTarget.classList.add("is-error")
    }
  }
}
